import React, { useCallback, useMemo } from 'react';
import { Client } from '../types/menu';
import { Button, Drawer, Form, Input, message } from 'antd';
import useOrderBasket from '../hooks/context/useOrderBasket';
import useHelpers from '../hooks/utility/useHelpers';
import ProductIdentifiers from '../components/menu/ProductIdentifiers';
import BasketHelpers from '../services/utils/basket/BasketHelpers';
import useData from '../hooks/utility/useData';
import { Server } from '../types/basket';
import ModifierGroup from '../components/menu/ModifierGroup';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import collect from 'collect.js';

type Props = {
  data: Server.OrderLineItem;
  onConfirm: (data: Server.OrderLineItem) => void;
  onClose: () => void;
};

export default function LineItemModal({ data, onConfirm, onClose }: Props) {
  const { compileItem } = useOrderBasket();
  const { formatCurrency } = useHelpers();

  const { data: lineItem, updateData: updateLineItem } = useData<Server.OrderLineItem>(data);

  const compiledItem = useMemo(
    () => compileItem(lineItem),
    [lineItem.product_id, lineItem.modifiers, lineItem.quantity],
  );

  const incrementQuantity = useCallback(
    () => updateLineItem('quantity', lineItem.quantity + 1),
    [lineItem.quantity],
  );
  const decrementQuantity = useCallback(
    () => lineItem.quantity !== 1 && updateLineItem('quantity', lineItem.quantity - 1),
    [lineItem.quantity],
  );

  const toggleModifier = useCallback(
    (modifierGroup: Client.ModifierGroup, modifier: Client.Modifier) =>
      updateLineItem(
        'modifiers',
        BasketHelpers.toggleModifier(lineItem.modifiers, modifierGroup, modifier),
      ),
    [lineItem.modifiers],
  );

  const _onConfirm = () => {
    const invalidModifierGroup = collect(compiledItem.product.modifier_groups)
      .filter((item) => BasketHelpers.isModifierGroupRequired(item))
      .first((item) => collect(lineItem.modifiers).where('modifier_group_id', item.id).isEmpty());

    if (invalidModifierGroup) {
      document
        .getElementById(`modifier-group-${invalidModifierGroup.id}`)
        ?.scrollIntoView({ behavior: 'smooth' });

      return message.error('Required modifier is missing');
    }

    onConfirm(lineItem);
    onClose();
  };

  return (
    <Drawer
      open
      width={window.innerWidth * 0.4}
      onClose={onClose}
      classNames={{ body: '!px-none' }}
      footer={
        <div className="flex justify-between">
          <div className="flex gap-lg border rounded-xl">
            <button className="my-auto p-sm" onClick={decrementQuantity}>
              <MinusOutlined />
            </button>
            <p className="my-auto">{lineItem.quantity}</p>
            <button className="my-auto p-sm" onClick={incrementQuantity}>
              <PlusOutlined />
            </button>
          </div>
          <Button
            onClick={_onConfirm}
            type="primary"
            size="large"
            className="min-h-full !h-auto font-medium"
          >
            {formatCurrency(compiledItem.package_price)}
          </Button>
        </div>
      }
    >
      <div className="px-lg">
        <div className="flex justify-between">
          <h6>{compiledItem.product.name}</h6>
          <h6>{formatCurrency(compiledItem.product.price)}</h6>
        </div>
        <ProductIdentifiers product={compiledItem.product} className="mt-xs" />
        <p className="mt-xs text-gray-300 text-sm">{compiledItem.product.description}</p>
        <Form.Item label="Note" layout="vertical" className="mt-xs">
          <Input.TextArea
            showCount
            maxLength={80}
            value={lineItem.note}
            onChange={(e) => updateLineItem('note', e.target.value)}
            placeholder="Specific item instructions"
            classNames={{
              count: '!bottom-0 !right-xs',
            }}
          />
        </Form.Item>
      </div>
      {compiledItem.product.modifier_groups.map((item) => (
        <ModifierGroup
          data={item}
          selectedModifiers={lineItem.modifiers}
          toggleModifier={toggleModifier}
          className="mt-md"
          key={`modifier-group-${item.id}`}
        />
      ))}
    </Drawer>
  );
}
